import { useEffect, useRef, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useParams } from 'react-router-dom'
import { Toaster } from 'sonner'
import { observer } from 'mobx-react-lite'
import HomePage from '@/pages/HomePage'
import PaymentProcessingPage from '@/pages/PaymentProcessingPage'
import { DepositPage } from '@/pages/DepositPage'
import { PaymentConfirmationPage } from '@/pages/PaymentConfirmationPage'
import WithdrawPageNew from '@/pages/WithdrawPageNew'
import CouponPage from '@/pages/CouponPage'
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'
import DashboardPage from '@/pages/DashboardPage'
import AffiliateWithdrawPage from '@/pages/AffiliateWithdrawPage'
import { PredictionsPage } from '@/pages/PredictionsPage'
import ProtectedRoute from '@/components/ProtectedRoute'
import PWAInstallPrompt from '@/components/PWAInstallPrompt'
import { InfoBanner, InfoBannerService, useAutoShowBanner } from '@/components/InfoBanner'
import { NotificationPermissionPrompt, useNotificationPrompt } from '@/components/NotificationPermissionPrompt'
import { NotificationToast } from '@/components/NotificationToast'

// Import services
import { analyticsService } from '@/services/analytics'
import { errorTracker } from '@/services/errorTracking'
import { initStorage } from '@/lib/storage'
import { useCoreController } from '@/services/coreController'
import { AuthProvider as OldAuthProvider } from '@/hooks/useAuth'
import { CaptureCommercialIdRedirect } from '@/components/CaptureCommercialIdRedirect'
import { AuthContext } from '@/contexts/authContext'
import useFindUser from '@/hooks/useFindUser'

// Wrapper component to handle commercial ID
const AppContent = observer(function AppContent() {
  const [searchParams] = useSearchParams()
  const { commercialId } = useParams()
  const coreController = useCoreController()
  const { banner, showBanner } = useAutoShowBanner()
  const bannerProcessedRef = useRef(false)

  // VAPID key - TODO: Replace with your actual VAPID key from Firebase Console
  // Get it from: Firebase Console → Project Settings → Cloud Messaging → Web configuration
  const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || 'YOUR_VAPID_KEY_HERE'

  // Setup notification prompt
  const { showPrompt, handleAllow, handleDeny, handleClose } = useNotificationPrompt(
    VAPID_KEY,
    async (token) => {
      console.log('✅ Notification permission granted, token:', token)
      // Token will be automatically sent to server by notificationService
    }
  )

  useEffect(() => {
    // Load application settings GLOBALLY (like Flutter does in constructor)
    console.debug('🔄 Loading application settings...');
    coreController.loadApplicationSetting();
  }, [coreController]);

  // Show banner if active (like Flutter's _showBannerIfActive)
  useEffect(() => {
    // Only process banner once to avoid infinite loop
    if (bannerProcessedRef.current) {
      return
    }

    console.debug('🔍 [Banner] Checking app settings...', {
      hasSettings: !!coreController.appSettingResponse,
      hasBanner: !!coreController.appSettingResponse?.banner,
      bannerData: coreController.appSettingResponse?.banner,
      isLoading: coreController.loading
    });

    // Only check for banner after settings are loaded
    if (!coreController.loading && coreController.appSettingResponse?.banner) {
      const bannerData = coreController.appSettingResponse.banner;
      console.debug('🎯 [Banner] Banner data found:', bannerData);

      const commercialId = coreController.getCommercialId() || '';
      console.debug('📍 [Banner] Commercial ID:', commercialId);

      const shown = InfoBannerService.showBannerIfActive(
        bannerData,
        commercialId,
        showBanner
      );

      console.debug('✅ [Banner] Show banner result:', shown);

      // Mark as processed to avoid infinite loop
      bannerProcessedRef.current = true
    }
  }, [coreController.loading, showBanner]);

  useEffect(() => {
    // Only react to explicit commercial ID sources: query param or the
    // :commercialId route param. Path-based capture is handled exclusively
    // by CaptureCommercialIdRedirect for /:commercialId and /share/:commercialId.
    // We intentionally do NOT depend on location.pathname here, otherwise
    // internal routes like /payment would be evaluated by getCommercialIdFromPath.
    const commercialIdFromQuery = searchParams.get('commercialId')
    const incoming = commercialIdFromQuery || commercialId || null

    if (!incoming) return

    const current = coreController.getCommercialId()
    if (current !== incoming) {
      coreController.setCommercialId(incoming)
      console.debug('📍 Commercial ID set from URL:', incoming)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, commercialId, coreController])

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/share/:commercialId" element={<CaptureCommercialIdRedirect />} />
        <Route path="/:commercialId" element={<CaptureCommercialIdRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/payment" element={<PaymentProcessingPage />} />
        <Route path="/deposit" element={<DepositPage />} />
        <Route path="/payment-confirmation" element={<PaymentConfirmationPage />} />
        <Route path="/withdraw" element={<WithdrawPageNew />} />
        <Route path="/coupons" element={<CouponPage />} />
        <Route path="/pronostics" element={<PredictionsPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parrainage"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate-withdraw"
          element={
            <ProtectedRoute>
              <AffiliateWithdrawPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Notification system */}
      {/* <NotificationToast />
      <NotificationPermissionPrompt
        open={showPrompt}
        onAllow={handleAllow}
        onDeny={handleDeny}
        onClose={handleClose}
      /> */}

      {/* Existing systems */}
      <Toaster position="top-right" richColors />
      <PWAInstallPrompt />

      {/* Banner system */}
      {banner && (
        <InfoBanner
          banner={banner}
          commercialId={coreController.getCommercialId() || ''}
        />
      )}
    </>
  )
});

function App() {
  const { user, setUser, isLoading } = useFindUser();

  // VAPID key from environment or placeholder
  const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || 'YOUR_VAPID_KEY_HERE'

  useEffect(() => {
    // Initialize storage
    initStorage().then(() => {
      console.debug('✅ Storage initialized')
    })

    // Initialize analytics
    analyticsService.initialize()

    // Initialize error tracker
    errorTracker.initialize()

    // Initialize push notifications (auto-request if previously granted)
    const initializeNotifications = async () => {
      try {
        const { initializeNotifications } = await import('@/services/notificationService')
        const result = await initializeNotifications(VAPID_KEY, false)

        if (result.success) {
          console.log('✅ Push notifications initialized successfully')
        } else {
          console.log('ℹ️ Push notifications not initialized:', result.error)
        }
      } catch (error) {
        console.debug('ℹ️ Could not initialize push notifications:', error)
      }
    }

    // Initialize notifications in background
    initializeNotifications()

    console.debug('🚀 App initialized with all services')
  }, [])

  return (
    <Router>
      <OldAuthProvider>
        <AuthContext.Provider value={{ user, setUser, isLoading }}>
          <AppContent />
        </AuthContext.Provider>
      </OldAuthProvider>
    </Router>
  )
}

export default App
